<template>
  <v-text-field
    ref="textField"
    :id="id"
    :disabled="disabled"
    :label="label"
    :name="name"
    :autocomplete="autocomplete"
    :value="value"
    :error="error"
    :rules="rules"
    :error-messages="errorMessages"
    :prefix="prefix"
    :suffix="suffix"
    :mask="mask"
    :placeholder="placeholder"
    :hint="hint"
    :type="type"
    :class="['text-input', `text-input--${color}`, {'text-input--bold': bold}, {'text-input--no-label': !label}, {'text-input--no-margin': noMargin}]"
    :validate-on-blur="validateOnBlur"
    :pattern="pattern"
    :maxlength="maxlength"
    :readonly="readonly"
    :loading="loading"
    :tabindex="tabindex"
    v-on="$listeners"
  />
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: undefined
    },
    autocomplete: {
      type: String,
      default: "off"
    },
    hint: {
      type: String,
      default: ""
    },
    mask: {
      type: String,
      default: ""
    },
    maxlength: {
      type: Number,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    value: {
      type: null,
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    label: {
      type: String,
      default: ""
    },
    error: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    pattern: {
      type: String,
      default: null
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    prefix: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: ""
    },
    bold: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: " "
    },
    noMargin: {
      type: Boolean,
      default: false
    },
    validateOnBlur: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "black",
      validators: value => {
        return ["black", "white"].includes(value);
      }
    },
    tabindex: {
      type: String,
      default: ""
    }
  },
  methods: {
    // Expose base method
    validate() {
      this.$refs.textField.validate();
    }
  }
};
</script>

<style lang="less" scoped>
@import "../assets/styles/_responsive.less";
@import "../assets/styles/_variables.less";

.text-input {
  font-size: 20px;
  line-height: 30px;
  margin-bottom: 25px;

  &--no-margin {
    margin-bottom: 0;
  }

  &--no-label {
    margin-top: 0;
  }

  &--bold {
    font-weight: @weight-bold;
  }

  &--black {
    color: @charcoal !important;

    /deep/ .v-input__slot {
      &:before {
        border-color: @mid-charcoal !important;
      }

      &:hover {
        &:before {
          border-color: @charcoal !important;
        }
      }
    }
  }

  &--white {
    color: @white !important;

    /deep/ .v-input__slot {
      &:before {
        border-color: @white !important;
      }

      .primary--text {
        color: @white !important;
      }
    }
  }

  /deep/ .v-messages {
    min-height: 15px;
    font-size: 15px;
    color: @mid-charcoal !important;

    &.error--text {
      color: @error-color !important;
    }
  }

  /deep/ input {
    color: currentColor !important;

    &:read-only {
      cursor: default;
    }

    // Transparent autocomplete background
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      animation: autofill 0s forwards;
    }

    @keyframes autofill {
      100% {
        background: transparent;
        color: inherit;
      }
    }
  }

  /deep/ .v-text-field {
    &__prefix,
    &__suffix {
      color: currentColor;
      padding: 0;
    }
  }

  /deep/ input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &.error--text {
    color: @error-color !important;

    /deep/ .v-input__slot {
      &:before {
        border-color: @error-color !important;
      }

      &:after {
        color: @error-color !important;
      }
    }
  }

  /deep/ .v-input__slot {
    &:after {
      color: currentColor;
    }
  }

  /deep/ .v-label {
    color: currentColor;
    font-size: 20px;
    font-weight: @weight-regular;
    cursor: text;

    &--active {
      font-weight: @weight-medium;
      transform: translateY(-20px) scale(0.6);
    }
  }
}
</style>
